import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Sparkles, ArrowUp, ArrowDown } from "lucide-react";

const SELECT = "mt-2 w-full rounded-none border border-input bg-secondary px-3 py-2 text-sm";

const TEMPLATE = [
  {
    label: "Sign up with code LiquidRP",
    instruction: "Screenshot the confirmation screen showing the code LiquidRP was applied.",
    points: 2000, proof_media: "image", max_files: 2,
    fields: [{ key: "", label: "Your username on the brand's site", type: "text", required: true }],
  },
  {
    label: "Make your first deposit",
    instruction: "Screenshot your deposit receipt — amount and date must be visible.",
    points: 3000, proof_media: "image", max_files: 2,
    fields: [{ key: "", label: "Deposit amount ($)", type: "number", required: true }],
  },
  {
    label: "Place a $10+ entry",
    instruction: "Screenshot the entry slip showing the stake and that it was accepted.",
    points: 5000, proof_media: "any", max_files: 3,
    fields: [{ key: "", label: "Entry amount ($)", type: "number", required: true }],
  },
];

const BLANK_STEP = {
  label: "", instruction: "", points: 1000, proof_media: "image", max_files: 1, fields: [],
};

export const QuestStepBuilder = ({ steps = [], stepMode = "sequential", onChange, onModeChange }) => {
  const total = steps.reduce((n, s) => n + (Number(s.points) || 0), 0);

  const setStep = (i, patch) =>
    onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const move = (i, dir) => {
    const next = [...steps];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="border border-border bg-secondary/30 p-5" data-testid="step-builder">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Verification steps
          </Label>
          <p className="mt-1 text-xs text-muted-foreground">
            Leave empty for a single proof upload. Add steps for multi-part offers like
            sign up → deposit → first bet.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" className="rounded-none"
            onClick={() => onChange([...steps, { ...BLANK_STEP }])} data-testid="step-add">
            <Plus className="mr-2 h-3.5 w-3.5" /> Add step
          </Button>
          <Button type="button" variant="outline" size="sm" className="rounded-none"
            onClick={() => onChange(TEMPLATE.map((s) => ({ ...s, fields: s.fields.map((f) => ({ ...f })) })))}
            data-testid="step-template">
            <Sparkles className="mr-2 h-3.5 w-3.5" /> 3-step offer template
          </Button>
        </div>
      </div>

      {steps.length > 0 && (
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              How players submit
            </Label>
            <select className={SELECT} value={stepMode} data-testid="step-mode"
              onChange={(e) => onModeChange(e.target.value)}>
              <option value="sequential">Per step, any order — each step pays out as it's approved</option>
              <option value="single">All at once — one submission, approved together</option>
            </select>
          </div>
          <div className="self-end text-sm">
            <span className="text-muted-foreground">Total to player: </span>
            <span className="mono text-primary" data-testid="step-total">{total.toLocaleString()} pts</span>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-4">
        {steps.map((s, i) => (
          <div key={`step-${i}`} className="border border-border bg-card p-4" data-testid={`step-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.22em] text-primary">Step {i + 1}</span>
              <div className="flex gap-1">
                <Button type="button" size="icon" variant="ghost" onClick={() => move(i, -1)}
                  data-testid={`step-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button type="button" size="icon" variant="ghost" onClick={() => move(i, 1)}
                  data-testid={`step-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                <Button type="button" size="icon" variant="ghost"
                  onClick={() => onChange(steps.filter((_, j) => j !== i))}
                  data-testid={`step-remove-${i}`}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Input className="rounded-none bg-secondary" placeholder="Step label (e.g. Make your first deposit)"
                value={s.label} onChange={(e) => setStep(i, { label: e.target.value })}
                data-testid={`step-label-${i}`} />
              <Input type="number" className="rounded-none bg-secondary mono" placeholder="Points"
                value={s.points} onChange={(e) => setStep(i, { points: Number(e.target.value) })}
                data-testid={`step-points-${i}`} />
              <div className="md:col-span-2">
                <Textarea className="rounded-none bg-secondary"
                  placeholder="What exactly must the screenshot or clip show?"
                  value={s.instruction} onChange={(e) => setStep(i, { instruction: e.target.value })}
                  data-testid={`step-instruction-${i}`} />
              </div>
              <select className="rounded-none border border-input bg-secondary px-3 py-2 text-sm"
                value={s.proof_media} onChange={(e) => setStep(i, { proof_media: e.target.value })}
                data-testid={`step-media-${i}`}>
                <option value="image">Images only</option>
                <option value="video">Video only</option>
                <option value="any">Image or video</option>
                <option value="none">No upload — fields only</option>
              </select>
              <select className="rounded-none border border-input bg-secondary px-3 py-2 text-sm"
                value={s.max_files} onChange={(e) => setStep(i, { max_files: Number(e.target.value) })}
                data-testid={`step-maxfiles-${i}`}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const label = `${n} file${n > 1 ? "s" : ""} max`;
                  return <option key={n} value={n}>{label}</option>;
                })}
              </select>
            </div>

            <div className="mt-4 border-t border-border/60 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Info the player must type
                </span>
                <Button type="button" size="sm" variant="ghost" className="rounded-none"
                  onClick={() => setStep(i, {
                    fields: [...(s.fields || []), { key: "", label: "", type: "text", required: true }],
                  })}
                  data-testid={`step-field-add-${i}`}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add field
                </Button>
              </div>
              {(s.fields || []).map((f, fi) => (
                <div key={`f-${i}-${fi}`} className="mt-2 flex flex-wrap items-center gap-2">
                  <Input className="max-w-xs rounded-none bg-secondary"
                    placeholder="Field label (e.g. PrizePicks username)"
                    value={f.label}
                    onChange={(e) => setStep(i, {
                      fields: s.fields.map((x, j) => (j === fi ? { ...x, label: e.target.value } : x)),
                    })}
                    data-testid={`step-field-label-${i}-${fi}`} />
                  <select className="rounded-none border border-input bg-secondary px-3 py-2 text-sm"
                    value={f.type}
                    onChange={(e) => setStep(i, {
                      fields: s.fields.map((x, j) => (j === fi ? { ...x, type: e.target.value } : x)),
                    })}
                    data-testid={`step-field-type-${i}-${fi}`}>
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                  </select>
                  <div className="flex items-center gap-2 text-xs">
                    <Switch checked={f.required !== false}
                      onCheckedChange={(v) => setStep(i, {
                        fields: s.fields.map((x, j) => (j === fi ? { ...x, required: v } : x)),
                      })}
                      data-testid={`step-field-required-${i}-${fi}`} />
                    required
                  </div>
                  <Button type="button" size="icon" variant="ghost"
                    onClick={() => setStep(i, { fields: s.fields.filter((_, j) => j !== fi) })}
                    data-testid={`step-field-remove-${i}-${fi}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
